// client/src/components/onboarding/WholesalerSelector.js
// ============================================
// WHOLESALER SELECTOR
// Searchable dropdown backed by adminAPI.getAllWholesalers
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../services/api';

const extractWholesalers = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.wholesalers)) return payload.wholesalers;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.wholesalers)) return payload.data.wholesalers;
  if (Array.isArray(payload.users)) return payload.users;
  return [];
};

const WholesalerSelector = ({ value, onChange, disabled }) => {
  const { t } = useTranslation('admin');
  const [query, setQuery] = useState('');
  const [wholesalers, setWholesalers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminAPI
      .getAllWholesalers({ limit: 500 })
      .then((res) => {
        if (cancelled) return;
        setWholesalers(extractWholesalers(res.data));
      })
      .catch(() => !cancelled && setWholesalers([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const idOf = (w) => w?.wholesalerId || w?._id || w?.id || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return wholesalers;
    return wholesalers.filter((w) => {
      const name = (w.companyName || w.wholesalerName || w.businessName || '').toLowerCase();
      const email = (w.email || w.user?.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [wholesalers, query]);

  const selected = useMemo(
    () => wholesalers.find((w) => idOf(w) === value),
    [wholesalers, value]
  );

  return (
    <div className="space-y-2">
      <input
        type="text"
        placeholder={t('conciergeOnboarding.form.placeholders.searchWholesaler', 'Search wholesaler…')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled || loading}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
      />
      <select
        value={value || ''}
        onChange={(e) => {
          const id = e.target.value;
          const w = wholesalers.find((x) => idOf(x) === id);
          onChange?.(id || null, w || null);
        }}
        disabled={disabled || loading}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
      >
        <option value="">
          {loading
            ? t('conciergeOnboarding.form.loading', 'Loading…')
            : t('conciergeOnboarding.form.placeholders.selectWholesaler', 'Select a wholesaler')}
        </option>
        {filtered.map((w) => {
          const id = idOf(w);
          if (!id) return null;
          const name = w.companyName || w.wholesalerName || w.businessName || w.email;
          return (
            <option key={id} value={id}>
              {name}
            </option>
          );
        })}
      </select>
      {selected && !selected.logo && !selected.logoUrl && (
        <p className="text-xs text-amber-700">
          {t(
            'conciergeOnboarding.form.noLogoWarning',
            'This wholesaler has no logo on file. If you pick "Wholesaler logo", the email will send without one.'
          )}
        </p>
      )}
    </div>
  );
};

export default WholesalerSelector;
